import { SiteUrls } from '@tableau/shared-urls';

import { RefreshContentScope } from '../../app/ActionPlaceScopes';
import { updateBreadcrumbs } from '../../app/actions/UpdateBreadcrumbsAction';
import { appStore } from '../../app/AppStore';
import { watchUrlForBreadcrumbUpdates } from '../../app/content/breadcrumbs/BreadcrumbHelpers';
import { BreadcrumbStateInfo } from '../../app/content/breadcrumbs/BreadcrumbStateInfo';
import { GroupGrid } from '../../app/content/GroupGrid';
import { IMainScope } from '../../app/IMainScope';
import { IPlaceTitleScope } from '../../app/IPlaceTitleScope';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { AngularToReactDataGridBridge } from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { ListResult } from '../fetchers/ListResult';
import { ActionButtonsHelper, IActionsMenuScope, SubplaceWithActionButtonsScope } from '../helpers/ActionButtonsHelper';
import { ActionContext } from '../helpers/ActionContext';
import { GroupContentType } from '../models/ContentTypes';
import { IEmptyTemplateModel } from '../models/IEmptyTemplate';
import { IGroupInfo } from '../models/IGroupInfo';
import { ITabsBannerModel } from '../models/ITabsBannerModel';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { Groups } from '../resources/Groups';
import { SiteRoleOptionsResource } from '../resources/SiteRoleOptions';
import { IGroup } from '../ServerApi/types/IGroup';
import { IActionButton, IListActionButton } from '../services/ActionPanelService';
import { SyncGroupEvent } from '../services/ActiveDirectoryGroupAction';
import { AddGroupsOptionsAction } from '../services/AddGroupsOptionsAction';
import { BrowserTitleService } from '../services/BrowserTitleService';
import { FilterPanelService } from '../services/FilterPanelService';
import { GroupActions } from '../services/GroupActions';
import { IQueryUpdater, QueryService } from '../services/QueryService';
import { ServerService } from '../services/ServerService';
import { SorterFactory } from '../services/SorterFactory';
import { ListActionsScope, SubplaceWithDataGridScope } from './ContentSubplaceCtrl';
import { IFilterPanelContainerScope } from './FilterPanelCtrl';

export interface IGroupsPlaceScope
extends IMainScope,
        IActionsMenuScope<IGroupInfo, {}>,
        IPlaceTitleScope,
        ListActionsScope,
        RefreshContentScope,
        SubplaceWithActionButtonsScope<IGroupInfo, {}>,
        SubplaceWithDataGridScope<IGroupInfo>,
        IFilterPanelContainerScope
{
  getDomainString(row: IGroup): void;
  canSessionUserManageUsers: boolean;
  isAD: boolean;
  showMinimumSiteRole: boolean;
  model: ITabsBannerModel;
}

export function getGroupsPlaceBreadcrumb(): BreadcrumbStateInfo {
  const siteUrls = siteUrlsSelector(appStore.getState());
  return{
    id: siteUrls && siteUrls.groupsUrl(),
    text: Messages.allGroups(),
    href: siteUrls && siteUrls.groupsUrl()
  };
}

export class GroupsPlaceCtrl {

  public static $inject = [
    '$location',
    '$scope',
    '$translate',
    'AngularToReactDataGridBridge',
    'BrowserTitleService',
    'Groups',
    'GroupActions',
    'AddGroupsOptionsAction',
    'QueryService',
    'SorterFactory',
    'ServerService',
    'FilterPanelService',
    'SiteRoleOptionsResource'
  ];

  private static emptyTemplateModel: IEmptyTemplateModel = { title: 'groupsEmptyMessage' };

  constructor(
    private $location: ng.ILocationService,
    private $scope: IGroupsPlaceScope,
    private $translate: ng.translate.ITranslateService,
    private AngularToReactDataGridBridge: AngularToReactDataGridBridge,
    BrowserTitleService: BrowserTitleService,
    private groups: Groups,
    private groupActions: GroupActions,
    addGroupsOptionsAction: AddGroupsOptionsAction,
    private queryService: QueryService,
    SorterFactory: SorterFactory,
    private ServerService: ServerService,
    filterPanelService: FilterPanelService,
    siteRoleOptionsResource: SiteRoleOptionsResource
  ) {

    const isActiveDirectory = this.ServerService.isServerDomainFamilyActiveDirectory();
    const canSessionUserManageUsers = this.ServerService.canSessionUserManageUsers();
    const showMinimumSiteRole = isActiveDirectory;

    BrowserTitleService.setTitleWithPlace({translatablePlaceName: 'groups'});

    $scope.model = { itemsCount: 0 };

    $scope.selectedItems = new Set<IGroup>((group: IGroup) => group.id);

    const actions = groupActions.getGroupsActionButtons();
    const getActionTarget = ActionButtonsHelper.getActionTargetFn($scope, $scope.selectedItems);

    this.configureForReactDataGrid(showMinimumSiteRole, actions, getActionTarget, siteRoleOptionsResource);

    $scope.filterControls = {};

    $scope.filterDefinitions = showMinimumSiteRole
      ? filterPanelService.getFilterDefinitions([ 'groupsMinimumSiteRole' ])
      : [];

    $scope.sorter = SorterFactory.create(GroupContentType.defaultOrder);
    $scope.$on('$destroy', () => $scope.sorter.detach());

    const queryUpdater = this.createQueryUpdater();

    $scope.sliceChanged = (newSlice: ListResult<IGroup>) => {
      $scope.selectedItems.updateItems(newSlice.items);
      $scope.model.itemsCount = newSlice.total;
    };

    $scope.refreshContent = () => {
      queryUpdater.refresh();
    };

    $scope.refreshContent();

    $scope.placeTitleClicked = () => {
      $scope.refreshContent();
    };

    $scope.$on(SyncGroupEvent.groupSynced, $scope.refreshContent);

    $scope.getDomainString = (row: IGroup) => {
      return row.domainName || $translate.instant('localDomain');
    };

    $scope.isAD = isActiveDirectory;

    ActionButtonsHelper.setupScopeForActionsMenuInActionBar({
      scope: $scope,
      isActionAvailable: action => action.isAvailable($scope.selectedItems),
      menuActions: actions,
      getActionTarget,
      translate: this.$translate.instant.bind(this.$translate)
    });

    $scope.listActions = [
      {
        name: 'add-groups',
        translate: 'addGroups',
        isAvailable: () => (isActiveDirectory && canSessionUserManageUsers),
        execute: ($event: JQueryEventObject) => {
          addGroupsOptionsAction.showAddGroupsOptionsDialog(ActionContext.fromEvent($event))
            .then((result: any) => {
              $scope.refreshContent();
            });
        }
      },
      {
        name: 'create-group',
        translate: 'createGroup',
        isAvailable: () => !(isActiveDirectory && canSessionUserManageUsers), // Go directly to the local group dialog if there is only one choice
        execute: ($event: JQueryEventObject) => {
          this.groupActions.createGroup($scope).then((result: any) => {
            $scope.refreshContent();
          });
        }
      }
    ];
    $scope.availableListAction = (listAction: IListActionButton) => listAction.isAvailable();

    $scope.emptyTemplateModel = GroupsPlaceCtrl.emptyTemplateModel;

    $scope.showMinimumSiteRole = showMinimumSiteRole;

    this.initBreadcrumbs();
  }

  private configureForReactDataGrid(showMinimumSiteRole: boolean,
                                    actions: IActionButton<IGroupInfo, {}>[],
                                    getActionTarget: () => IActionTarget<IGroupInfo, {}>,
                                    siteRoleOptionsResource: SiteRoleOptionsResource) {

    const siteUrls = new SiteUrls({
      siteUrlName: this.ServerService.sessionSiteUrlName(),
      queryString: window.location.search
    });

    const columns = GroupGrid.columns(
      this.$translate.instant.bind(this.$translate),
      this.$scope,
      showMinimumSiteRole,
      this.ServerService.isServerDomainFamilyActiveDirectory(),
      actions,
      getActionTarget,
      siteUrls,
      siteRoleOptionsResource);

    this.AngularToReactDataGridBridge.attachReactDataGridToScope({
      $scope: this.$scope,
      columns,
      emptyChild: this.AngularToReactDataGridBridge.emptyTemplateElementFor(GroupsPlaceCtrl.emptyTemplateModel),
      canShowFilterPanel: GroupContentType.canShowFilterPanel,
      headerHeight: CommonStyles.Sizes.DataGridWithPictureIconsHeaderHeight,
      rowOuterHeight: CommonStyles.Sizes.DataGridWithPictureIconsRowHeight
    });
  }

  private createQueryUpdater(): IQueryUpdater {
    return this.queryService.createQueryUpdater(
      this.$scope,
      GroupContentType,
      this.groups,
      (resourceQuery) => {

        this.AngularToReactDataGridBridge.updateReactDataGridRowStore({ scope: this.$scope, sliceable: resourceQuery });

        this.$scope.sliceable = resourceQuery;
        this.$scope.sorter.setOrder(resourceQuery.getOrder());
        resourceQuery.getCount().then((result: number) => {
          this.$scope.model.itemsCount = result;
        });
      },
      {}
    );

  }

  private initBreadcrumbs() {
    const breadcrumbs: BreadcrumbStateInfo[] = [ getGroupsPlaceBreadcrumb() ];
    appStore.dispatch(updateBreadcrumbs(breadcrumbs));

    const currentPath = this.$location.path();
    watchUrlForBreadcrumbUpdates(this.$scope, this.$location, () => this.$location.path() === currentPath);
  }

}

Module.instance().controller('GroupsPlaceCtrl', GroupsPlaceCtrl);
